import React, { createContext, useContext, useState, useEffect } from 'react';
import useApi from '../../../../utils/api-manager/Helper/useApi';

const ShiftContext = createContext();

export const useShift = () => {
  const context = useContext(ShiftContext);
  if (!context) {
    throw new Error('useShift must be used within a ShiftProvider');
  }
  return context;
};

export const ShiftProvider = ({ children }) => {
  const [shiftData, setShiftData] = useState({
    title: '',
    is_active: false,
    shift_id: null,
    shift_start_at: null
  });
  const [activeShiftId, setActiveShiftId] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [initialized, setInitialized] = useState(false); 
  const { Get, Patch, Post } = useApi();

  const checkActiveShift = async () => {
    try {
      setLoading(true);
      const response = await Get('isShiftActive');

      if (response.message === "You already have an active shift") {
        setShiftData({
          title: response.new_shift_title,
          is_active: true,
          shift_id: response.shift_id,
          shift_start_at: response.shift_start_at
        });
        setActiveShiftId(response.shift_id);
      } else {
        setShiftData({
          title: response.new_shift_title || '',
          is_active: false,
          shift_id: response.shift_id || null,
          shift_start_at: response.shift_start_at || null
        });
        setActiveShiftId(null);
      }
    } catch (error) {
      console.error('Error checking active shift:', error);

      setShiftData({
        title: '',
        is_active: false,
        shift_id: null,
        shift_start_at: null
      });
      setActiveShiftId(null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

const startShift = async (selectedDate, selectedTime) => {
  try {
    const payload = {};
    if (selectedDate) payload.date = selectedDate;
    if (selectedTime) payload.time = selectedTime;

    const response = await Post('createShiftBusinessReportAndRegisters', payload);
    if (response.status === 201) {
      const newShiftId = response.data.shift_id;
      setActiveShiftId(newShiftId);

      // `Get()` already unwraps to the response BODY (not the axios
      // response), so checking `shiftActiveResponse.status`/`.data` here
      // was always false/undefined - shiftData.is_active never actually
      // flipped to true after a successful start, even though the shift
      // itself was created fine on the backend. Reuse the already-correct
      // parsing in checkActiveShift() instead of duplicating it.
      await checkActiveShift();
      return { success: true };
    }
    return { success: false };
  } catch (error) {
    console.error('Error creating shift report:', error);
    return { success: false, error: error?.response?.data?.detail || error?.message || 'Error creating shift.' };
  }
};


  const endShift = async () => {
    try {
      if (!activeShiftId) {
        return { success: false, error: 'No active shift ID available' };
      }

      const response = await Patch('shiftData', activeShiftId, {
        is_active: false,
      });

      if (response.status === 200) {
        setShiftData(prev => ({
          ...prev,
          is_active: false,
        }));
        setActiveShiftId(null);
        return { success: true };
      }
      return { success: false, error: 'Failed to end shift' };
    } catch (error) {
      console.error('Error ending shift:', error);
      return { success: false, error: 'Error ending shift: ' + error };
    }
  };

  useEffect(() => {
    checkActiveShift();
  }, []);

  const contextValue = {
    shiftData,
    activeShiftId,
    loading,
    initialized, 
    startShift,
    endShift,
    checkActiveShift,
    setShiftData
  };

  return (
    <ShiftContext.Provider value={contextValue}>
      {children}
    </ShiftContext.Provider>
  );
};